/** The minimum a folder needs for tree maths — both folder trees satisfy it. */
export interface FolderNode {
  id: string
  name: string
  parent: string | null
}

/**
 * Folders grouped by parent id.
 *
 * Siblings are sorted by name by default. Pass `sort: false` when the server
 * already ordered them — the vault puts real folders ahead of its type buckets,
 * and re-sorting here would interleave them.
 */
export function groupFoldersByParent<T extends FolderNode>(
  folders: T[],
  { sort = true }: { sort?: boolean } = {}
): Map<string | null, T[]> {
  const byParent = new Map<string | null, T[]>()
  for (const folder of folders) {
    const siblings = byParent.get(folder.parent) ?? []
    siblings.push(folder)
    byParent.set(folder.parent, siblings)
  }
  if (sort) {
    for (const siblings of byParent.values()) {
      siblings.sort((a, b) => a.name.localeCompare(b.name))
    }
  }
  return byParent
}

/** A folder's id plus every descendant's — what a move must not target. */
export function collectSubtreeIds<T extends FolderNode>(folders: T[], folderId: string): Set<string> {
  const byParent = groupFoldersByParent(folders)
  const ids = new Set<string>([folderId])
  const queue = [folderId]
  while (queue.length) {
    const current = queue.shift() as string
    for (const child of byParent.get(current) ?? []) {
      if (ids.has(child.id)) continue
      ids.add(child.id)
      queue.push(child.id)
    }
  }
  return ids
}

/** Ancestor ids, nearest parent first. Stops on a cycle rather than looping. */
export function ancestorIds<T extends FolderNode>(folders: T[], folderId: string): string[] {
  const byId = new Map(folders.map((f) => [f.id, f]))
  const chain: string[] = []
  let current = byId.get(folderId)?.parent ?? null
  while (current && !chain.includes(current)) {
    chain.push(current)
    current = byId.get(current)?.parent ?? null
  }
  return chain
}
